// --- React ---
import React, { useState, useEffect } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// --- 공통 UI 컴포넌트 ---
import { ModalShell, FormStack, FormField, FormLabel } from '@/components/ui';
import { Button, Input } from '@/components/ui';
import { PhoneInput, normalizePhone } from '@/components/ui/common/PhoneInput';

// --- 스토어 ---
import { useNotificationStore } from '../../../store/notificationStore';
import { useAppStore } from '../../../store/loadingStore';

// --- API ---
import { useUpdateUser, useUpdateUserById } from '@/api/auth/useAuth';

// --- 인증 스토어 ---
import { useAuthStore } from '@/store/authStore';

// --- 유효성 검사 · 에러 파싱 ---
import { parseCompanyApiError } from '@/utils/errors';

// ==================== 타입 정의 ====================

interface CompanyManagerInfo {
  id: number;
  name: string;
  email: string;
  phone: string | null;
}

interface CompanyManagerEditModalProps {
  isOpen: boolean;
  companyManager: CompanyManagerInfo | null;
  onClose: () => void;
  onSave: () => void;
}

// ==================== 컴포넌트 ====================

const CompanyManagerEditModal: React.FC<CompanyManagerEditModalProps> = ({
  isOpen,
  companyManager,
  onClose,
  onSave,
}) => {
  // ==================== 다국어 ====================
  const { t } = useTranslation();

  // ==================== 스토어 ====================
  const { showSuccess, showError } = useNotificationStore();
  const { showLoading, hideLoading } = useAppStore();

  // ==================== 상태 관리 ====================
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
  });
  const [phoneValid, setPhoneValid] = useState(true);

  // ==================== API 훅 ====================
  const currentUser = useAuthStore(s => s.user);
  const updateSelfMutation = useUpdateUser();
  const updateOtherMutation = useUpdateUserById();
  const isEditingSelf = !!companyManager && currentUser?.id === companyManager.id;

  // ==================== 이펙트 ====================

  /** 모달 열림 / 매니저 변경 시 폼 데이터 동기화 */
  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      name: companyManager?.name ?? '',
      phone: normalizePhone(companyManager?.phone ?? ''),
    });
    setPhoneValid(true);
  }, [isOpen, companyManager]);

  // ==================== 핸들러 ====================

  /**
   * 저장 핸들러
   * 1. 본인 수정인지 타인 수정인지 판별
   * 2. 해당 mutation 호출 → 3. 성공/실패 알림
   */
  const handleSave = async () => {
    if (!companyManager) return;

    try {
      showLoading();
      const payload = {
        name: formData.name.trim() || undefined,
        phone: formData.phone.trim() || undefined,
      };
      if (isEditingSelf) {
        await updateSelfMutation.mutateAsync(payload);
      } else {
        await updateOtherMutation.mutateAsync({ userId: companyManager.id, ...payload });
      }
      showSuccess('company:company_manager_update_success');
      onSave();
      onClose();
    } catch (error: unknown) {
      const e = parseCompanyApiError(error);
      showError(e.title, e.message);
    } finally {
      hideLoading();
    }
  };

  /** 모달 닫기 — 폼 데이터 초기화 후 부모 onClose 호출 */
  const handleClose = () => {
    setFormData({ name: '', phone: '' });
    setPhoneValid(true);
    onClose();
  };

  // ==================== 렌더링 ====================

  return (
    <ModalShell
      isOpen={isOpen}
      title={t(i18nKeys.company.company_manager_edit)}
      onClose={handleClose}
      size="md"
      footer={
        <>
          {/* 취소 버튼 */}
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={updateSelfMutation.isPending || updateOtherMutation.isPending}
          >
            {t(i18nKeys.company.cancel)}
          </Button>
          {/* 저장 버튼 */}
          <Button
            onClick={handleSave}
            disabled={
              updateSelfMutation.isPending ||
              updateOtherMutation.isPending ||
              (!!formData.phone && !phoneValid)
            }
          >
            {updateSelfMutation.isPending || updateOtherMutation.isPending
              ? t(i18nKeys.company.processing)
              : t(i18nKeys.company.save)}
          </Button>
        </>
      }
    >
      <FormStack>
        {/* 이메일 (읽기 전용) */}
        <FormField>
          <FormLabel>{t(i18nKeys.auth.email)}</FormLabel>
          <Input
            value={companyManager?.email || ''}
            onChange={() => {}}
            disabled
            size="md"
            fullWidth
          />
        </FormField>

        {/* 이름 */}
        <FormField>
          <FormLabel>{t(i18nKeys.auth.name)}</FormLabel>
          <Input
            value={formData.name}
            onChange={value => setFormData({ ...formData, name: value })}
            placeholder={t(i18nKeys.company.site_manager_name_placeholder)}
            size="md"
            fullWidth
          />
        </FormField>

        {/* 전화번호 */}
        <FormField>
          <PhoneInput
            label={t(i18nKeys.auth.phone)}
            value={formData.phone}
            onChange={(value, isValid) => {
              setFormData({ ...formData, phone: value });
              setPhoneValid(isValid);
            }}
            fullWidth
          />
        </FormField>
      </FormStack>
    </ModalShell>
  );
};

export default CompanyManagerEditModal;
